import type { AxiosResponse } from "axios";
import type BackOfficeConnector from "data/backOfficeConnector";
import type EventBus from "services/eventBus";
import RepositoryBase from "./repositoryBase";
import type { IPagination } from "data/types";
import { serializeFilters } from "utils/filters";
import type BankExportFilters from "entities/filters/bankExportFilter";
import TransferRefundExportDetailResponse from "entities/transferRefundExportDetailResponse";
import TransferRefundExportResponse from "entities/transferRefundExportResponse";
import PayGateRefundExportDetailResponse from "entities/payGateRefundExportDetailResponse";
import InternalTransferExportResponse from "entities/internalTransferExportResponse";
import InternalTransferExportDetailResponse from "entities/internalTransferExportDetailResponse";
import PayGateRefundExportResponse from "entities/payGateRefundExportResponse";
import DistributorRefundExportDetailResponse from "entities/distributorRefundExportDetailResponse";
import DistributorRefundExportResponse from "entities/distributorRefundExportResponse";

export default class BankExportRepository extends RepositoryBase<BackOfficeConnector> {
  constructor(protected apiFactory: ReturnType<typeof BackOfficeConnector.Factory>, protected eventBus: EventBus) {
    super(apiFactory, eventBus);
  }

  allBankTransfers(pagination: IPagination, filters: BankExportFilters) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("transfer_refund_export_search")
          .postPagedEntities<TransferRefundExportResponse>(
            { ...serializeFilters(filters, false), ...pagination },
            TransferRefundExportResponse
          ),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  getBankTransferById(id: string) {
    return this.callApi(api =>
      api.all("finance").one("transfer_refund_exports", id).getEntity(TransferRefundExportDetailResponse)
    );
  }

  getBankTransferReport(exportId: string): Promise<AxiosResponse> {
    return this.callApi(api => api.all("finance").all("transfer_refund_exports").one(exportId).all("report").getRaw() as any);
  }

  allCardReturns(pagination: IPagination, filters: BankExportFilters) {
    return this.callApi(
      api =>
        api
          .all("finance")
          .all("paygate_refund_export_search")
          .postPagedEntities<PayGateRefundExportResponse>(
            {
              ...serializeFilters(filters, false),
              ...pagination,
            },
            PayGateRefundExportResponse
          ),
      { ownApiFactory: () => this.apiFactory({ customVersion: "1.1" }) }
    );
  }

  getCardReturnById(id: string) {
    return this.callApi(api => api.all("finance").one("paygate_refund_exports", id).getEntity(PayGateRefundExportDetailResponse));
  }

  getCardReturnReport(exportId: string) {
    return this.callApi(api => api.all("finance").all("paygate_refund_exports").one(exportId).all("report").getRaw() as any);
  }

  allInternalTransfers(pagination: IPagination, filters: BankExportFilters) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("internal_transfer_exports")
        .postPagedEntities<InternalTransferExportResponse>(
          { ...serializeFilters(filters, false), ...pagination },
          InternalTransferExportResponse
        )
    );
  }

  getInternalTransferById(id: string) {
    return this.callApi(api =>
      api.all("finance").one("internal_transfer_exports", id).getEntity(InternalTransferExportDetailResponse)
    );
  }

  getInternalTransferReport(exportId: string) {
    return this.callApi(api => api.all("finance").all("internal_transfer_exports").one(exportId).all("report").getRaw() as any);
  }

  allDistributorRefundExports(pagination: IPagination, filters: BankExportFilters) {
    return this.callApi(api =>
      api
        .all("finance")
        .all("distributor_refund_exports")
        .postPagedEntities<DistributorRefundExportResponse>(
          { ...serializeFilters(filters, false), ...pagination },
          DistributorRefundExportResponse
        )
    );
  }

  getDistributorRefundExportById(id: string) {
    return this.callApi(api =>
      api.all("finance").one("distributor_refund_exports", id).getEntity(DistributorRefundExportDetailResponse)
    );
  }

  getDistributorRefundExportReport(exportId: string) {
    return this.callApi(api => api.all("finance").one("distributor_refund_exports", exportId).all("report").getRaw() as any);
  }
}
